﻿import { ObjectUtils } from '../main/ObjectUtils.js';
import { ProductDrawingViewModel } from './ProductDrawingViewModel.js';
import { AnyProductInteraction } from './ProductInteraction.js';
import { ProductZoomer } from './ProductZoomer.js';

/**
 * The SimpleProductPreviewer can be used on any page where we need to display the product drawing.
 */
export class SimpleProductPreviewer extends ProductDrawingViewModel {
  constructor(drawing, name) {
    super(drawing, name);
    drawing.setIsInteractive(false);
  }

  isDrawingVisible() {
    // TODO: Add the correct logic for detecting the drawing visibility here
    //const activeTabLink = document.querySelector('#showcase-tabs a.active[data-toggle="tab"]');
    //return ObjectUtils.isAssigned(activeTabLink) && activeTabLink.id === this.previewTabLinkId;
    return true;
  }

  /**
   * This is a handler for this.drawing.onProductDisplayed event.
   */
  onProductDisplayed(drawing, product) {
    drawing.resizeCanvasToFitAroundStage(true);
  }
}

/**
 * The ProductPreviewer is hard-coupled to a UI containing a Bootstrap tab control with a Product drawing
 * inside one of the tabs. This drawing is used for previewing the currently edited product.
 */
export class ProductPreviewer extends ProductDrawingViewModel {
  constructor(drawing, name, config) {
    super(drawing, name);
    this.config = config;
    if (drawing) {
      if (drawing.engine.handleMaximizeClickExternally) {
        drawing.setIsInteractive(false);
      } else {
        this.addMaximizeClickHandler();
      }
    }

    this.previewTabLinkId = 'preview-tab-link'; // The actual tab
    this.previewTabId = 'PreviewTab'; // The tab contents (panel)
    this.previewMaxLink = document.getElementById('preview-max-link');
    this.commonImageGroup = 'Common';

    this.setupShowcaseTabs();
    this.setupMainTabCarousel();

    if (drawing) {
      // Enable the debug toggle:
      const drawingElem = this.drawing.engine.productDrawing;
      const parentDiv = this.closest(drawingElem, el => el.nodeName == 'DIV');
      //if (drawingElem && drawingElem.parentElement && drawingElem.parentElement.parentElement) {
      if (parentDiv) {
        //const debugCheck = drawingElem.parentElement.parentElement.querySelector('.debug-toggle');
        const debugCheck = parentDiv.querySelector('.debug-toggle');
        if (debugCheck) {
          debugCheck.addEventListener('click', event => {
            //alert(debugCheck.checked);
            this.toggleDebugView(parentDiv, debugCheck.checked);
          })
        }
      }
    }
  }

  // Helper: find the nearest parent element of el
  closest(el, fn) {
    return el && (fn(el) ? el : this.closest(el.parentNode, fn));
  }

  toggleDebugView(targetDiv, show) {
    const style = targetDiv.style;
    const canvasContainer = targetDiv.querySelector('.canvas-container');
    if (show) {
      this.beforeDebug = {
        position: style.position,
        zIndex: style.zIndex,
        width: style.width,
        height: style.height,
        top: style.top,
        left: style.left,
        targetParent: targetDiv.parentElement,
        targetNextSibling: targetDiv.nextSibling,
      };
      style.position = 'absolute';
      style.zIndex = '9999';
      style.width = '100%';
      style.height = '100%';
      style.top = '0';
      style.left = '0';
      if (canvasContainer) {
        this.beforeDebug.contHeight = canvasContainer.style.height;
        this.beforeDebug.contHeightPri = canvasContainer.style.getPropertyPriority('height');
        canvasContainer.style.setProperty('height', '100%', 'important');
      }
      document.body.appendChild(targetDiv);
      this.drawing.resizeMonitor.onResize();
      this.drawing.engine.toggleDebugView(true);
    } else if (this.beforeDebug) {
      this.drawing.engine.toggleDebugView(false);
      style.position = this.beforeDebug.position;
      style.zIndex = this.beforeDebug.zIndex;
      style.width = this.beforeDebug.width;
      style.height = this.beforeDebug.height;
      style.top = this.beforeDebug.top;
      style.left = this.beforeDebug.left;
      if (canvasContainer) {
        canvasContainer.style.setProperty('height', this.beforeDebug.contHeight, this.beforeDebug.contHeightPri);
      }
      this.beforeDebug.targetParent.insertBefore(targetDiv, this.beforeDebug.targetNextSibling);
      this.drawing.resizeMonitor.onResize();
    }
  }

  showProductCanvasFullScreen(drawingElement) {
    if (this.drawing.engine.canDoFullScreen) {
      this.drawing.engine.toggleFullScreen();
      return;
    }

    // Remove resize listener because zoomer uses its own resize listener
    this.drawing.resizeMonitor.toggleMonitor(false);

    const wasInteractive = this.drawing.isInteractive;
    if (wasInteractive) {
      this.drawing.setIsInteractive(false);
    }

    this.zoomer.onActivated = () => {
      this.zoomer.onActivated = null;
      this.drawing.engine.bestFitStageAndCanvas(true, true);
    };

    this.zoomer.onDeactivate = () => {
      this.zoomer.onDeactivate = null;
      drawingElement.classList.remove('zoom-target');
      this.drawing.resizeMonitor.toggleMonitor(true);
      if (wasInteractive) {
        this.drawing.setIsInteractive(true);
      }
      this.drawing.engine.bestFitStageAndCanvas(true, true);
    };

    drawingElement.classList.add('zoom-target'); // Needed by the Zoomer js code
    this.zoomer.assignTargetElement(drawingElement);
    this.zoomer.activateFullscreen();
  }

  addMaximizeClickHandler() {
    this.zoomer = new ProductZoomer();

    const behavior = new AnyProductInteraction();
    // Delegate event add/removal to the drawing engine:
    behavior.toggleInteractive = (displayObject, on, clickHandler) =>
      this.drawing.engine.toggleDisplayObjectInteractive(displayObject, on, clickHandler);

    // Note, this will set the interaction behavior's product property to the drawing's product:
    this.drawing.addProductInteraction(behavior);
    this.drawing.setIsInteractive(true);
    this.drawing.engine.hoverCursor = ''; // Assume the cursor will be set by our css

    // Must be at end because we are overriding the default onInteraction handler:
    behavior.onInteraction = (beh, event) => {
      // Don't think we need this: event.stopImmediatePropagation(); // Block PhotoSwiper from showing its full screen UI
      // We don't care which component or element was clicked because we know we are going to display
      // the entire drawing:
      //const drawingElement = event.currentTarget;
      const drawingElement = this.drawing.engine.getRootHtmlElement(this.drawing.product.displayObject);
      this.showProductCanvasFullScreen(drawingElement);
    };
  }

  refreshFullSizeImageLink() {
    /* previewMaxLink is an html tag. The tag's href and data-size attributes are used by the
     * photoswipe library when displaying the image in full screen mode. */
    if (this.previewMaxLink) {
      // TODO: Review code and ensure this method isn't called more often than necessary
      // TODO: Consider using resizeCanvasToFitInFullScreen (entire screen) instead of resizeCanvasToFitInFullWindow (browser window)
      this.drawing.resizeCanvasToFitInFullWindow(true);
      try {
        // The drawing was maximized in the call above, let's get its size:
        const size = this.drawing.getDrawingSize();
        // TODO: Consider making this a JIT update of the href done after the user clicks on the link.
        this.previewMaxLink.href = this.drawing.getDrawingImageUrl();
        this.previewMaxLink.attributes['data-size'].value = `${size.width}x${size.height}`;
      } finally {
        this.drawing.bestFitStageAndCanvas(true);
      }
    }
  }

  isDrawingVisible() {
    const activeTabLink = document.querySelector(this.activeTabSelector);
    return ObjectUtils.isAssigned(activeTabLink) && activeTabLink.id === this.previewTabLinkId;
  }

  /**
   * This is a handler for this.drawing.onProductDisplayed event.
   */
  onProductDisplayed(drawing, product) {
    // This is performance heavy, lets defer to next cycle
    requestAnimationFrame(() => {
      this.refreshFullSizeImageLink();
    });
  }

  isDefaultProduct() {
    return this.onGetIsDefaultProduct && this.onGetIsDefaultProduct();
  }

  //#region Showcase Tabs

  getActiveShowcaseTabName() {
    const tab = jQuery(this.activeTabSelector);
    return tab ? tab.attr('href') : null;
  }

  setupShowcaseTabs() {
    let selector = '#' + this.config.UI.showCaseTabs;
    this.allTabsSelector = `${selector} a[data-toggle="tab"]`;
    this.activeTabSelector = `${selector} a.active[data-toggle="tab"]`;
    this.getTabSelector = (tabName) => `${selector} a[href="${tabName}"]`;

    jQuery(this.allTabsSelector).on('shown.bs.tab', async (e) => {
      if (e.target.id === this.previewTabLinkId) {
        await this.displayCurrentProduct();
        /* This is a work around for a delay before the canvas' container's clientWidth is updated
         * to the correct width. */
        this.drawing.doIfCanvasContainerWidthUpdatesWithin(1000, () => {
          //console.log('Canvas container width did update!')
          this.drawing.bestFitStageAndCanvas(true);
        });
      }
      if (this.onShowcaseTabShown) {
        this.onShowcaseTabShown(e);
      }
    });

    selector = '.' + this.config.UI.showCaseTabPanes;
    this.allTabPanesSelector = `${selector} div[role="tabpanel"]`;
  }

  activateShowcaseTab(tabName) {
    const tab = jQuery(this.getTabSelector(tabName));
    if (tab && tab.length > 0) {
      tab.tab('show');
    }
  }

  activatePreviewTab() {
    const tab = jQuery('#' + this.previewTabLinkId);
    if (tab && tab.length > 0) {
      tab.tab('show');
    }
  }

  //#endregion

  //#region Main Tab Carousel

  // This is the Owl carousel/slider displaying the product's images (under the first tab after the preview tab)

  removeImagesNotNeeded($currentCarouselItems, productVariantId) {
    const notNeededIndeces = [];
    $currentCarouselItems.each((index, galleryItem) => {
      const notNeeded = galleryItem.dataset.group !== productVariantId && galleryItem.dataset.group !== this.commonImageGroup;
      if (notNeeded) {
        const ndx = this.$mainCarouselItems.index(galleryItem);
        notNeededIndeces.push(ndx);
      }
    });
    return notNeededIndeces;
  }

  addNeededImages($currentCarouselItems, productVariantId) {
    const toBeAddedIndeces = [];
    this.$mainCarouselItems.each((index, galleryItem) => {
      const isNeeded = galleryItem.dataset.group === productVariantId || galleryItem.dataset.group === this.commonImageGroup;
      if (isNeeded) {
        let alreadyThere = false;
        $currentCarouselItems.each((index, item) => {
          if (item === galleryItem) {
            alreadyThere = true;
          }
        });
        if (!alreadyThere) {
          toBeAddedIndeces.push(index);
        }
      }
    });
    return toBeAddedIndeces;
  }

  addCarouselItem(ndx) {
    let item = this.$mainCarouselItems[ndx];
    this.$mainCarousel.trigger('add.owl.carousel', [item]);
    const thumb = this.$mainCarouselThumbs[ndx];
    thumb.classList.toggle('d-none', false);
    thumb.classList.toggle('active', ndx === 0);
  }

  removeCarouselItem(ndx) {
    // Remove carousel image:
    this.$mainCarousel.trigger('remove.owl.carousel', [ndx]);
    // Remove thumbnail image:
    const thumb = this.$mainCarouselThumbs[ndx];
    thumb.classList.toggle('d-none', true);
    thumb.classList.toggle('active', false);
  }

  //removeAllMainCarouselSlides() {
  //  let $owlItems = this.$mainCarousel.find('.owl-item');
  //  let count = $owlItems.length;
  //  while (count > 0) {
  //    this.$mainCarousel.trigger('remove.owl.carousel', [0]);
  //    count--;
  //  }
  //  $owlItems = this.$mainCarousel.find('.owl-item');
  //  count = $owlItems.length;
  //}

  updateVisibleImages(productVariantId) {
    const $owlItems = this.$mainCarousel.find('.owl-item .gallery-item');
    const notNeededIndeces = this.removeImagesNotNeeded($owlItems, productVariantId);
    //this.removeAllMainCarouselSlides();
    const toBeAddedIndeces = this.addNeededImages($owlItems, productVariantId);
    window.requestAnimationFrame(() => {
      for (var i = notNeededIndeces.length - 1; i >= 0; i--) {
        this.removeCarouselItem(notNeededIndeces[i]);
      }

      toBeAddedIndeces.forEach(ndx => {
        this.addCarouselItem(ndx);
      });
      this.$mainCarousel.trigger('refresh.owl.carousel');
      this.activateFirstMainCarouselItem();
    });
  }

  activateFirstMainCarouselItem() {
    this.$mainCarousel.trigger('to.owl.carousel', [0]);
    let isFirst = true;
    this.$mainCarouselThumbs.each((index, thumb) => {
      if (isFirst && !thumb.classList.contains('d-none')) {
        thumb.classList.toggle('active', true);
        isFirst = false;
      } else {
        thumb.classList.toggle('active', false);
      }
    })
  }

  setupMainTabCarousel() {
    const $allTabPanes = jQuery(this.allTabPanesSelector);
    const $mainTabPane = $allTabPanes.first();
    if ($mainTabPane.length) {
      this.$mainCarousel = $mainTabPane.find('.owl-carousel');
      if (this.$mainCarousel) {
        const doSetupCarousel = () => {
          this.$mainCarouselItems = this.$mainCarousel.find('.owl-item .gallery-item');
          this.$mainCarouselThumbs = $mainTabPane.find('.product-thumbnails li');
          window.requestAnimationFrame(() => {
            if (this.apiProduct) {
              const notNeededIndeces = this.removeImagesNotNeeded(this.$mainCarouselItems, this.apiProduct.variantId);
              for (var i = notNeededIndeces.length - 1; i >= 0; i--) {
                this.removeCarouselItem(notNeededIndeces[i]);
              }
            }
            this.$mainCarousel.trigger('refresh.owl.carousel');
            this.activateFirstMainCarouselItem();
          });
        }
        if (this.$mainCarousel.hasClass('owl-loaded')) {
          doSetupCarousel();
        } else {
          this.$mainCarousel.on('initialized.owl.carousel', event => {
            doSetupCarousel();
          });
        }
      }
    }
  }

  //#endregion Main Tab Carousel

  //#region Preview Carousel

  // This is the Owl carousel/slider displaying the product's preview image and/or drawing

  setupPreviewCarousel() {
    this.$previewCarousel = jQuery(`#${this.previewTabId} .owl-carousel`);
    if (this.$previewCarousel.length > 1) {
      throw new Error('There can only be one preview tab. Maybe you chose the wrong kind of tab when setting up the showcase tabs?');
    }
    this.previewItemCount = 0;

    const setupPreviewCarousel = (itemCount) => {
      this.isPreviewCarouselInitialized = true;
      this.previewItemCount = itemCount;
      /* Looks like updating the carousel from within this event handler doesn't work.
       * Hence, push the update to the next frame: */
      console.log('[event: owl.carousel.initialized] (async:) ProductPageViewModel.constructor: window.requestAnimationFrame()');
      window.requestAnimationFrame(() => {
        if (this.onPreviewCarouselInitialized) {
          this.onPreviewCarouselInitialized();
        }
      });
    }

    if (this.$previewCarousel.hasClass('owl-loaded')) {
      console.log('Carousel is already initialized');
      const items = this.$previewCarousel.find('.owl-item');
      setupPreviewCarousel(items.length);
    } else {
      this.$previewCarousel.on('initialized.owl.carousel', event => {
        setupPreviewCarousel(event.item.count);
      });
    }
  }

  /**
   * Display the correct image in the preview carousel. If the current product equals the default,
   * then display the predefined high quality photo. Otherwise, display the generated drawing.
   * @param {any} forceDrawing If true, then display the drawing regardless.
   */
  displayPreviewImage(forceDrawing) {
    if (this.previewItemCount > 0) {
      let index;
      if (forceDrawing) {
        index = this.previewItemCount - 1;
      } else if (this.isDefaultProduct() || this.previewItemCount < 2) {
        index = 0;
      } else {
        index = this.previewItemCount - 1;
      }
      this.$previewCarousel.trigger('to.owl.carousel', [index]);
    }
  }

  //#endregion

  displayEditedProduct(product, isUserDriven) {
    if (this.isDisplayingProduct) {
      // Re-entrant call!
      console.log('(async) previewer.displayEditedProduct: previewer.scheduleProductDisplay().')
      this.scheduleProductDisplay(isUserDriven, usrDriven => {
        console.group('(callback: previewer.scheduleProductDisplay) previewer.displayEditedProduct: previewer.displayProduct()...')
        this.displayEditedProduct(product, usrDriven);
        console.groupEnd();
      });
    } else {
      this.displayPreviewImage(/*forceDrawing:*/ false);
      if (product.variantId) {
        this.updateVisibleImages(product.variantId);
      }
      console.log('(async) previewer.displayEditedProduct: previewer.displayProduct(...)...')
      // Async call:
      /* await */ this.displayProduct(product);
      if (isUserDriven && this.previewItemCount > 0) {
        /* If the reason for the display call was driven by the user, then also switch from the
         * "inspiration" tab to the preview tab (if needed), and show the scroll-to-drawing button. */
        this.activateShowcaseTab(this.config.UI.previewTabName);
      }
    }
  }

  async doStart(apiProduct, isProductRestorePending) {
    /* If the owl carousel isn't initialized yet, then there's no reason to display the product yet
     * (it'll be done in the initialized event handler in the constructor above). Also, if there's
     * a product restore pending, then the product will be displayed after restore has completed
     * (see productEditor.onProductChanged event handler in the constructor above). Hence, don't
     * display the product now. */
    this.displayProductWhenStarting = this.isPreviewCarouselInitialized && !isProductRestorePending;
    console.log(`SKIP displaying the product? ${!this.displayProductWhenStarting}.`);
    await super.doStart(apiProduct);
    if (this.isPreviewCarouselInitialized) {
      this.displayPreviewImage(/*forceDrawing:*/ false);
    }
  }
}
